import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import { newsContentLooksLikeHtml, sanitizeNewsHtml } from './newsHtml';
import { displayNewsDate, isValidIsoDate } from './isoDateDisplay';
import { apiUrl } from './apiBase';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';

function CalendarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M8 5V3M16 5V3M4 9h16M6 5h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const newsArticleProseClass =
  'text-base leading-relaxed text-neutral-700 sm:text-[1.0625rem] [&_p]:mb-5 [&_p:last-child]:mb-0 [&_a]:font-semibold [&_a]:text-brand-olive [&_a]:underline [&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-lime/70 [&_blockquote]:pl-4 [&_blockquote]:text-neutral-600 [&_h2]:mb-3 [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-neutral-900 [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-neutral-900 [&_li]:my-1 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6';

function ArticleBody({ text }) {
  const raw = typeof text === 'string' && text.trim() ? text.trim() : '';
  if (!raw) return null;
  const blocks = raw.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  const paragraphs =
    blocks.length > 0 ? blocks : raw.split('\n').map((l) => l.trim()).filter(Boolean);

  return (
    <>
      {paragraphs.map((p, i) => (
        <p key={i} className="mb-5 text-base leading-relaxed text-neutral-700 last:mb-0 sm:text-[1.0625rem]">
          {p}
        </p>
      ))}
    </>
  );
}

export default function NewsDetail() {
  const { articleId } = useParams();
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSite)
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const decodedId = articleId ? decodeURIComponent(articleId) : '';
  const article = site.news.articles.find((a) => a.id === decodedId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [articleId]);

  useEffect(() => {
    if (!ready) return;
    const base = 'Rafid Seed';
    if (!article) {
      document.title = `Article not found | ${base}`;
      return () => {
        document.title = base;
      };
    }
    document.title = `${article.title} | ${base}`;
    return () => {
      document.title = base;
    };
  }, [ready, article]);

  if (!ready) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-white text-neutral-500">Loading…</div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <MarketingSiteHeader
          site={site}
          idPrefix="news-detail"
          alwaysSolid
          isNavLinkCurrent={marketingHeaderNavIsCurrent}
        />
        <div className="mx-auto max-w-2xl px-4 py-20 pt-[calc(5rem+env(safe-area-inset-top))] text-center sm:px-6 sm:pt-[calc(5.5rem+env(safe-area-inset-top))]">
          <h1 className="text-2xl font-bold text-neutral-900">Article not found</h1>
          <p className="mt-3 text-neutral-600">This story may have been removed or the link is incorrect.</p>
          <Link
            to="/#blog"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-lime no-underline hover:text-brand-lime-hover"
          >
            ← Back to news
          </Link>
        </div>
      </div>
    );
  }

  const { date, dateIso, title, excerpt, image, alt, readMoreHref, body } = article;
  const bodyTrim = typeof body === 'string' ? body.trim() : '';
  const excerptTrim = typeof excerpt === 'string' ? excerpt.trim() : '';
  const showLead = Boolean(bodyTrim && excerptTrim);
  const mainText = bodyTrim || excerptTrim;
  const mainHtmlSafe = sanitizeNewsHtml(mainText);
  const excerptHtmlSafe = sanitizeNewsHtml(excerptTrim);
  const newsDateLabel = displayNewsDate({ dateIso, date });

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="news-detail"
        alwaysSolid
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <article className="bg-gradient-to-b from-neutral-50 via-white to-white pt-[calc(4.25rem+env(safe-area-inset-top))] sm:pt-[calc(4.5rem+env(safe-area-inset-top))]">
        <div className="mx-auto max-w-shell px-4 pt-8 sm:px-6 sm:pt-10 lg:px-8">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-[0_24px_60px_-35px_rgba(20,20,20,0.35)]">
            <div className="bg-neutral-100 p-2 sm:p-3">
              <img
                src={image}
                alt={alt || ''}
                className="mx-auto block h-auto w-full rounded-2xl object-contain object-center"
              />
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-shell px-4 pb-20 pt-10 sm:px-6 sm:pt-12 md:pb-24 lg:px-8">
          <div className="mx-auto max-w-5xl">
            {newsDateLabel ? (
              <p className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-500">
                <CalendarIcon className="h-4 w-4 shrink-0 text-brand-lime" />
                <time dateTime={isValidIsoDate(dateIso) ? String(dateIso).trim() : undefined}>
                  {newsDateLabel}
                </time>
              </p>
            ) : null}
            <h1 className="mb-6 text-3xl font-bold leading-[1.15] tracking-tight text-neutral-900 sm:mb-8 sm:text-4xl md:text-[2.35rem] md:leading-[1.12]">
              {title}
            </h1>
            {showLead ? (
              newsContentLooksLikeHtml(excerptTrim) ? (
                <div
                  className={`border-l-4 border-brand-lime/80 pl-4 text-lg font-medium leading-relaxed text-neutral-600 sm:text-xl ${newsArticleProseClass}`}
                  dangerouslySetInnerHTML={{ __html: excerptHtmlSafe }}
                />
              ) : (
                <p className="whitespace-pre-line border-l-4 border-brand-lime/80 pl-4 text-lg font-medium leading-relaxed text-neutral-600 sm:text-xl">
                  {excerptTrim}
                </p>
              )
            ) : null}

            <div className={showLead ? 'mt-10 border-t border-neutral-100 pt-10' : 'mt-8'}>
              {newsContentLooksLikeHtml(mainText) && mainHtmlSafe ? (
                <div
                  className={newsArticleProseClass}
                  dangerouslySetInnerHTML={{ __html: mainHtmlSafe }}
                />
              ) : (
                <ArticleBody text={mainText} />
              )}
            </div>

            <div className="mt-12 flex flex-wrap gap-4 border-t border-neutral-100 pt-10">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 rounded-2xl border-0 bg-brand-lime px-5 py-2.5 text-sm font-semibold text-brand-forest no-underline transition-colors hover:bg-brand-lime-hover"
              >
                ← All news
              </Link>
              <a
                href={readMoreHref || '#contact'}
                className="inline-flex items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 no-underline transition-colors hover:bg-neutral-50"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
